import { useEffect } from 'react';

interface OrganizationSchemaProps {
  type: 'organization';
}

interface ProductSchemaProps {
  type: 'product';
  name: string;
  image: string;
  description: string;
  price: string;
  currency: string;
  availability: boolean;
  url: string;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  type: 'breadcrumb';
  items: BreadcrumbItem[];
}

interface ArticleSchemaProps {
  type: 'article';
  title: string;
  description: string;
  url: string;
  image: string;
  publishedAt: string;
  author: string;
}

interface WebSiteSchemaProps {
  type: 'website';
}

interface LocalBusinessSchemaProps {
  type: 'localBusiness';
}

type StructuredDataProps = OrganizationSchemaProps | ProductSchemaProps | BreadcrumbSchemaProps | ArticleSchemaProps | WebSiteSchemaProps | LocalBusinessSchemaProps;

const BASE_URL = 'https://sampaolicreazioni.it';

function getSchemaJSON(props: StructuredDataProps): object {
  switch (props.type) {
    case 'organization':
      return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Sampaoli Creazioni',
        url: BASE_URL,
        logo: `${BASE_URL}/favicon.png`,
        founder: {
          '@type': 'Person',
          name: 'Andrea Sampaoli',
        },
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Teolo',
          addressRegion: 'Veneto',
          addressCountry: 'IT',
        },
        description: 'Handcrafted bronze hunting jewelry, wildlife pins and mountain accessories. Made in Italy.',
        sameAs: [
          'https://www.instagram.com/sampaolicreazioni/',
          'https://www.facebook.com/sampaolicreazioni',
        ],
      };

    case 'product':
      return {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: props.name,
        image: props.image,
        description: props.description,
        brand: {
          '@type': 'Brand',
          name: 'Sampaoli Creazioni',
        },
        offers: {
          '@type': 'Offer',
          price: props.price,
          priceCurrency: props.currency,
          availability: props.availability
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          url: props.url,
          seller: {
            '@type': 'Organization',
            name: 'Sampaoli Creazioni',
          },
        },
      };

    case 'breadcrumb':
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: props.items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`,
        })),
      };

    case 'article':
      return {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: props.title,
        description: props.description,
        image: props.image,
        url: props.url,
        datePublished: props.publishedAt,
        author: {
          '@type': 'Person',
          name: props.author,
        },
        publisher: {
          '@type': 'Organization',
          name: 'Sampaoli Creazioni',
          logo: {
            '@type': 'ImageObject',
            url: `${BASE_URL}/favicon.png`,
          },
        },
      };

    case 'website':
      return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Sampaoli Creazioni',
        url: BASE_URL,
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${BASE_URL}/shop?q={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      };

    case 'localBusiness':
      return {
        '@context': 'https://schema.org',
        '@type': 'JewelryStore',
        name: 'Sampaoli Creazioni',
        url: BASE_URL,
        logo: `${BASE_URL}/favicon.png`,
        image: `${BASE_URL}/favicon.png`,
        description: 'Gioielli artigianali in bronzo ispirati alla caccia, alla fauna selvatica e alla montagna. Handcrafted bronze hunting and wildlife jewelry, Made in Italy.',
        priceRange: '€€',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Via Roma',
          addressLocality: 'Teolo',
          addressRegion: 'Veneto',
          postalCode: '35037',
          addressCountry: 'IT',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: 45.3497,
          longitude: 11.6722,
        },
        sameAs: [
          'https://www.instagram.com/sampaolicreazioni/',
          'https://www.facebook.com/sampaolicreazioni',
        ],
      };
  }
}

export function StructuredData(props: StructuredDataProps) {
  const schema = getSchemaJSON(props);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [JSON.stringify(schema)]);

  return null;
}
